import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import {
  Building2, Video, Award, Star,
  Workflow, Users2, Leaf, Boxes,
  Layers, Compass, Sparkles, ClipboardList,
  Laptop, GraduationCap,
  Quote
} from "lucide-react";

const IMPACT_STATS = [
  { icon: Building2, value: "250+", label: "Enterprise Clients", desc: "Organizations across industries rely on Skillsgain for enterprise learning initiatives." },
  { icon: Video, value: "500+", label: "Instructor-Led Workshops", desc: "Interactive learning experiences delivered by certified trainers and industry experts." },
  { icon: Award, value: "100+", label: "Enterprise Trainers", desc: "Experienced professionals delivering practical, business-focused learning programs." },
  { icon: Star, value: "98%", label: "Client Satisfaction", desc: "Consistently recognized for engaging learning experiences and measurable results." }
];

const LEARNING_OUTCOMES = [
  { icon: Workflow, title: "Agile Methodologies", desc: "Adopt Agile principles, iterative planning, continuous feedback, and collaborative delivery practices that improve project execution, team alignment, and customer value." },
  { icon: Users2, title: "Agile & Scrum", desc: "Learn Scrum roles, sprint planning, daily stand-ups, backlog management, retrospectives, and Agile ceremonies to improve project delivery and cross-functional collaboration." },
  { icon: Leaf, title: "Spring Framework", desc: "Develop enterprise applications using the Spring Framework, Spring Boot, REST APIs, dependency injection, microservices, and cloud-ready application architecture." },
  { icon: Boxes, title: ".NET Framework", desc: "Master the .NET Framework, C#, ASP.NET, ADO.NET, object-oriented programming, and enterprise application development using Microsoft's technology stack." },
  { icon: Layers, title: "Blended Learning Methodology", desc: "Combine instructor-led sessions, virtual classrooms, self-paced modules, hands-on labs, and assessments to deliver engaging blended learning experiences for enterprise teams." },
  { icon: Compass, title: "Experiential Learning", desc: "Strengthen practical knowledge through simulations, enterprise case studies, coding exercises, collaborative workshops, and real-world project assignments that encourage continuous skill application." },
  { icon: Sparkles, title: "AI-Powered Learning Platform", desc: "Personalize learning with an AI Learning Platform that recommends relevant content, tracks learner progress, provides intelligent assessments, and supports continuous skill development." },
  { icon: ClipboardList, title: "Project Management & Learning Frameworks", desc: "Develop practical project execution skills through structured learning frameworks, Project Management Courses, modern training methodologies, governance practices, and collaborative planning techniques." }
];

const DELIVERY_MODELS = [
  { icon: Video, title: "Live Virtual Learning", desc: "Interactive instructor-led online sessions with collaborative discussions, activities, and practical exercises." },
  { icon: Building2, title: "Classroom Learning", desc: "Face-to-face workshops featuring group activities, expert mentoring, and enterprise case studies." },
  { icon: Laptop, title: "Self-Paced Learning", desc: "Access digital learning resources, recorded sessions, quizzes, and assignments anytime." },
  { icon: GraduationCap, title: "Cohort-Based Learning", desc: "Structured learning journeys where teams progress together through workshops, assessments, and collaborative projects." }
];

const BY_THE_NUMBERS = [
  { value: "100,000+", label: "Learning Hours Delivered" },
  { value: "500+", label: "Enterprise Batches Delivered" },
  { value: "25+", label: "Global Technology Partners" },
  { value: "50+", label: "Fortune & High-Growth Companies Served" }
];

const MethodologiesFrameworksPage = () => {
  return (
    <div className="bg-white min-h-screen">
      <Header/>

      <PageHero
        eyebrow="ENTERPRISE LEARNING METHODOLOGIES & FRAMEWORKS"
        title="Modern Learning Methodologies & Frameworks for Enterprise Success"
        subtitle="Enable continuous learning with Skillsgain's enterprise programs focused on Employee Training Methods, Blended Learning, Experiential Learning, Project Management Courses, AI Learning Platforms, and personalized learning experiences. Our methodology combines instructor-led sessions, digital learning, hands-on practice, and measurable outcomes to enhance employee performance and organizational growth."
      />

      {/* Enterprise Learning Impact */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-xs font-bold tracking-widest text-[#4F9622] mb-3">ENTERPRISE LEARNING IMPACT</div>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">Learning experiences trusted by organizations worldwide</h2>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {IMPACT_STATS.map((o, i) => (
            <Reveal key={o.label} variant="up" delay={i * 80}>
              <div className="sg-card bg-white border border-gray-100 rounded-2xl p-6 h-full">
                <div className="w-11 h-11 rounded-lg bg-[#6BBE32]/15 flex items-center justify-center mb-4">
                  <o.icon size={20} className="text-[#6BBE32]"/>
                </div>
                <div className="text-3xl font-bold text-[#15348C]">{o.value}</div>
                <div className="text-sm font-semibold text-[#15348C] mt-1">{o.label}</div>
                <p className="text-xs text-gray-500 mt-2 leading-relaxed">{o.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Learning Approaches */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-xs font-bold tracking-widest text-[#4F9622] mb-3">LEARNING APPROACHES</div>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">Practical methodologies designed for modern enterprises</h2>
            <p className="mt-3 text-gray-600">Combine instructor expertise, technology, collaboration, and real-world application to create meaningful learning experiences across every stage of professional development.</p>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {LEARNING_OUTCOMES.map((m, i) => (
            <Reveal key={m.title} variant="up" delay={(i % 4) * 80}>
              <div className="sg-card group bg-[#F8F9FA] border border-gray-100 rounded-2xl p-6 h-full">
                <div className="w-11 h-11 rounded-lg bg-[#15348C] flex items-center justify-center mb-4 group-hover:bg-[#6BBE32] transition-colors">
                  <m.icon size={20} className="text-white"/>
                </div>
                <h4 className="font-bold text-[#15348C]">{m.title}</h4>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">{m.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Learning Experiences */}
      <section className="bg-[#F8F9FA] py-16">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-12">
              <div className="text-xs font-bold tracking-widest text-[#4F9622] mb-3">LEARNING EXPERIENCES</div>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">Training methods aligned with business objectives</h2>
              <p className="mt-3 text-gray-600">Select the learning format that best matches organizational goals, learner preferences, and project timelines while ensuring consistent engagement and measurable progress.</p>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {DELIVERY_MODELS.map((m, i) => (
              <Reveal key={m.title} variant="up" delay={i * 80}>
                <div className="flex flex-col gap-3 bg-white border border-gray-100 rounded-xl p-5 h-full sg-card">
                  <div className="w-10 h-10 shrink-0 rounded-lg bg-[#6BBE32]/15 flex items-center justify-center">
                    <m.icon size={18} className="text-[#6BBE32]"/>
                  </div>
                  <div className="font-bold text-[#15348C] text-sm">{m.title}</div>
                  <p className="text-xs text-gray-500 leading-relaxed">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* By the Numbers + Customer Success Story */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-3 gap-8">
          <Reveal variant="up" className="lg:col-span-1">
            <div className="bg-[#15348C] text-white rounded-2xl p-8 h-full relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-48 h-48 bg-[#6BBE32]/20 rounded-full blur-3xl"/>
              <div className="relative">
                <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-3">BY THE NUMBERS</div>
                <div className="space-y-5">
                  {BY_THE_NUMBERS.map(s => (
                    <div key={s.label}>
                      <div className="text-3xl font-bold">{s.value}</div>
                      <div className="text-xs text-gray-300">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal variant="up" delay={100} className="lg:col-span-2">
            <div className="bg-[#F8F9FA] border border-gray-100 rounded-2xl p-8 h-full flex flex-col">
              <Quote size={32} className="text-[#4F9622] mb-4"/>
              <div className="text-xs font-bold tracking-widest text-[#4F9622] mb-3">CUSTOMER SUCCESS STORY</div>
              <p className="text-lg lg:text-xl text-[#15348C] leading-relaxed">
                Skillsgain introduced a blended enterprise learning approach combining instructor-led workshops, AI-assisted learning, and practical projects. This methodology improved learner engagement, knowledge retention, and application of new skills across multiple business teams.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 pb-20">
        <Reveal variant="scale">
          <div className="bg-[#15348C] rounded-3xl p-10 lg:p-14 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#6BBE32]/20 rounded-full blur-3xl"/>
            <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-[#8BD835]/20 rounded-full blur-3xl"/>
            <div className="relative grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-3">FOR L&D & BUSINESS LEADERS</div>
                <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight">Modern Learning Methodologies for Enterprise Teams</h2>
                <p className="mt-3 text-gray-300">Enhance learning outcomes with Employee Training Methods, Blended Learning, Experiential Learning, Project Management Courses, personalized learning environments, and AI-powered learning experiences.</p>
              </div>
              <div className="flex flex-wrap gap-3 lg:justify-end">
                <Link to="/contact" className="sg-btn-glow bg-[#8BD835] hover:bg-[#6BBE32] text-white font-semibold px-6 py-3 rounded-md transition-colors">Talk to Our Learning Experts</Link>
                <Link to="/contact" className="border-2 border-white/40 text-white font-semibold px-6 py-3 rounded-md hover:bg-white/10 hover:border-[#8BD835] transition-colors">Request Corporate Proposal</Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <Footer/>
    </div>
  );
};

export default MethodologiesFrameworksPage;
